// src/admin/pages/AdminUsers.js
import React, { useState, useEffect, useContext } from "react";
import AdminSidebar from "../AdminSidebar";
import AdminHeader from "../AdminHeader";
import {
  FaEyeSlash ,
  FaEye,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaTrash,
  FaUserShield,
  FaUserSlash,
  FaPlus,
  FaKey,
  FaUsers,
  FaCheck,
} from "react-icons/fa";
import { AdminLanguageContext } from "../context/AdminLanguageContext";
import {
  getAdminUsers,
  deleteAdminUser,
  toggleAdminUserStatus,
  createAdminUser,
  resetAdminUserPassword,
} from "../../services/adminUsers.api";
import {
  getStaffPermissions,
  updateStaffPermissions,
} from "../../services/adminStaffPermissions.api";
import {
  FaBox,
  FaShoppingCart,
  FaUserFriends,
  FaEnvelope,
  FaBullhorn,
  FaImage,
  FaTruck,
  FaChartLine,
  FaSearch,
  FaUsersCog,
} from "react-icons/fa";

function AdminUsers() {
  const [isOpen, setIsOpen] = useState(false);
  const { lang } = useContext(AdminLanguageContext);

  const overlayRef = React.useRef(null);

  const adminRole = React.useMemo(() => {
    try {
      const token = localStorage.getItem("adminToken");
      if (!token) return null;

      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload.role;
    } catch {
      return null;
    }
  }, []);

  const [permUser, setPermUser] = useState(null);

  const [permissions, setPermissions] = useState({
    can_manage_products: false,
    can_manage_orders: false,
    can_manage_customers: false,
    can_manage_messages: false,
    can_manage_marketing: false,
    can_manage_banners: false,
    can_manage_shipping: false,
    can_view_analytics: false,
    can_manage_seo: false,
    can_manage_staff: false,
  });

  const openPermissions = async (user) => {
    setPermUser(user);
    const data = await getStaffPermissions(user.id);

    setPermissions({
      can_manage_products: !!data?.can_manage_products,
      can_manage_orders: !!data?.can_manage_orders,
      can_manage_customers: !!data?.can_manage_customers,
      can_manage_messages: !!data?.can_manage_messages,
      can_manage_marketing: !!data?.can_manage_marketing,
      can_manage_banners: !!data?.can_manage_banners,
      can_manage_shipping: !!data?.can_manage_shipping,
      can_view_analytics: !!data?.can_view_analytics,
      can_manage_seo: !!data?.can_manage_seo,
      can_manage_staff: !!data?.can_manage_staff,
    });
  };

  const TEXT = {
    EN: {
      title: "Users",
      search: "Search by name or email...",
      allRoles: "All roles",
      admin: "Admin",
      staff: "Staff",
      customer: "Customer",
      allStatuses: "All statuses",
      active: "Active",
      suspended: "Suspended",
      name: "Name",
      email: "Email",
      role: "Role",
      status: "Status",
      created: "Created",
      actions: "Actions",
      loading: "Loading users...",
      empty: "No users found.",
      delete: "Delete",
      deleteTitle: "Delete User?",
      deleteText: "Are you sure? This action cannot be undone.",
      cancel: "Cancel",
      prev: "Prev",
      next: "Next",
      addUser: "Add User",
      username: "Username",
      password: "Password",
      emailOptional: "Email (optional)",
      create: "Create",
      creating: "Creating...",
      permissions: "Permissions",
      confirmPassword: "Confirm Password",
confirmPasswordError: "Passwords do not match",
passwordRequired: "Password is required",
usernameRequired: "Username is required",
      save: "Save",
      all: "All",
      toggleTitle: "Change User Status?",
      resetPassword: "Reset Password",
resetPasswordTitle: "Reset Password",
resetPasswordSaved: "Password updated successfully",
toggleToActiveText: "Are you sure you want to activate this user?",
toggleToSuspendedText: "Are you sure you want to suspend this user?",
statusChangedToActive: "User activated successfully",
statusChangedToSuspended: "User suspended successfully",
userDeleted: "User deleted successfully",
      usersCount: "users",
      noEmail: "No email",
      notAllowed: "You are not allowed to manage users and staff.",
    },
    AR: {
      title: "المستخدمون",
      search: "ابحث بالاسم أو البريد الإلكتروني...",
      allRoles: "جميع الأدوار",
      admin: "مدير",
      confirmPassword: "تأكيد كلمة المرور",
confirmPasswordError: "كلمات المرور غير متطابقة",
passwordRequired: "كلمة المرور مطلوبة",
usernameRequired: "اسم المستخدم مطلوب",
      staff: "موظف",
      customer: "عميل",
      allStatuses: "جميع الحالات",
      active: "نشط",
      suspended: "موقوف",
      name: "الاسم",
      email: "البريد الإلكتروني",
      role: "الدور",
      status: "الحالة",
      created: "تاريخ الإنشاء",
      actions: "الإجراءات",
      loading: "جارٍ تحميل المستخدمين...",
      empty: "لا يوجد مستخدمون.",
      delete: "حذف",
      deleteTitle: "حذف المستخدم؟",
      deleteText: "هل أنت متأكد؟ لا يمكن التراجع عن هذا الإجراء.",
      cancel: "إلغاء",
      prev: "السابق",
      next: "التالي",
      addUser: "إضافة مستخدم",
      username: "اسم المستخدم",
      password: "كلمة المرور",
      emailOptional: "البريد الإلكتروني (اختياري)",
      create: "إنشاء",
      creating: "جارٍ الإنشاء...",
      permissions: "الصلاحيات",
      save: "حفظ",
      all: "الكل",
      toggleTitle: "تغيير حالة المستخدم؟",
      resetPassword: "إعادة تعيين كلمة المرور",
resetPasswordTitle: "إعادة تعيين كلمة المرور",
resetPasswordSaved: "تم تحديث كلمة المرور بنجاح",
toggleToActiveText: "هل أنت متأكد أنك تريد تفعيل هذا المستخدم؟",
toggleToSuspendedText: "هل أنت متأكد أنك تريد إيقاف هذا المستخدم؟",
statusChangedToActive: "تم تفعيل المستخدم بنجاح",
statusChangedToSuspended: "تم إيقاف المستخدم بنجاح",
userDeleted: "تم حذف المستخدم بنجاح",
      usersCount: "مستخدم",
      noEmail: "بدون بريد",
      notAllowed: "غير مصرح لك بإدارة المستخدمين والموظفين",
    },
  }[lang];

  const adminUser = JSON.parse(localStorage.getItem("adminUser") || "{}");
  const perms = JSON.parse(localStorage.getItem("staffPermissions") || "{}");
  const isAdmin = adminUser.role === "admin";
  const canAccess = isAdmin || perms.can_manage_staff;

  const [toggleUser, setToggleUser] = useState(null);
  const [toast, setToast] = useState(null);
  const [hoverUser, setHoverUser] = useState(null);

  
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);

  const [roleFilter, setRoleFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [loading, setLoading] = useState(true);

  const [showNewPassword, setShowNewPassword] = useState(false);
const [confirmPassword, setConfirmPassword] = useState("");
const firstInputRef = React.useRef(null);

  const [showResetPassword, setShowResetPassword] = useState(false);

  const [sortBy, setSortBy] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
 
  const [page, setPage] = useState(1);
  const perPage = 8;

  const [deleteId, setDeleteId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [creating, setCreating] = useState(false);

  const [resetUser, setResetUser] = useState(null);
const [resettingPassword, setResettingPassword] = useState(false);

const [resetForm, setResetForm] = useState({
  id: null,
  username: "",
  email: "",
  role: "staff",
  is_active: true,
  password: "",
});

const showToastMessage = (message, type = "success") => {
  setToast({ message, type });
  setTimeout(() => setToast(null), 3000);
};

  const [newUser, setNewUser] = useState({
    username: "",
    email: "",
    password: "",
    role: "staff",
    is_active: true,
  });

  useEffect(() => {
  if (showAdd && firstInputRef.current) {
    setTimeout(() => {
      firstInputRef.current.focus();
    }, 100);
  }
}, [showAdd]);

  useEffect(() => {
  const handleClickOutside = (e) => {
    if (!overlayRef.current) return;

    if (!overlayRef.current.contains(e.target)) {
      setHoverUser(null);
    }
  };

  if (hoverUser) {
    document.addEventListener("mousedown", handleClickOutside);
  }

  return () => {
    document.removeEventListener("mousedown", handleClickOutside);
  };
}, [hoverUser]);

  useEffect(() => {
    if (!canAccess) {
      setLoading(false);
      setUsers([]);
      return;
    }

    setLoading(true);

    getAdminUsers()
      .then((data) =>
        setUsers(
          (data || [])
    .filter(Boolean) // 💥 removes undefined/null
    .map((u) => ({
  id: u.id,
  name: u.name || "",
  email: u.email || "",
  role: u.role || "staff",
  status: u.status || "active",
  createdAt: u.createdAt || new Date().toISOString(),
  passwordHash: u.passwordHash || "", // ✅ ADD
}))
        )
      )
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [adminRole]);

  useEffect(() => {
    setPage(1);
  }, [search, roleFilter, statusFilter, sortBy, sortDirection]);

  const openResetPasswordModal = (user) => {
  setHoverUser(null);

  setResetUser(user);
  setShowResetPassword(false); // ✅ reset visibility

  setResetForm({
    id: user.id,
    username: user.name || "",
    email: user.email || "",
    role: user.role || "staff",
    is_active: user.status === "active",
    password: "",
  });
};

const submitResetPassword = async () => {
  if (!resetForm.password.trim()) {
    showToastMessage(TEXT.passwordRequired, "error");
    return;
  }

  try {
    setResettingPassword(true);

    const updated = await resetAdminUserPassword(resetForm.id, {
      password: resetForm.password.trim(),
    });

    setUsers((prev) =>
      prev.map((u) =>
        u.id === resetForm.id
          ? {
              ...u,
              passwordHash: updated.passwordHash || u.passwordHash || "",
            }
          : u
      )
    );

    setResetUser(null);
setShowResetPassword(false);
setResetForm({
  id: null,
  username: "",
  email: "",
  role: "staff",
  is_active: true,
  password: "",
});

   showToastMessage(
  lang === "EN"
    ? "User password updated"
    : "تم تحديث كلمة مرور المستخدم"
);
  } catch (e) {
    showToastMessage(e.message, "error");
  } finally {
    setResettingPassword(false);
  }
};


  const filtered = (users || []).filter((u) => {
  if (!u) return false; // 💥 CRITICAL FIX

  const q = String(search ?? "").toLowerCase();

  const name = String(u.name ?? "").toLowerCase();
  const email = String(u.email ?? "").toLowerCase();

  return (
    (name.includes(q) || email.includes(q)) &&
    (roleFilter === "All" || u.role === roleFilter) &&
    (statusFilter === "All" || u.status === statusFilter)
  );
});

  const sorted = [...filtered].sort((a, b) => {
  if (!sortBy) return 0;

  // SAFE getter
  const getSafeValue = (obj, key) => {
    const val = obj?.[key];

    if (val === null || val === undefined) return "";

    if (key === "createdAt") {
      return new Date(val).getTime() || 0;
    }

    if (typeof val === "string") return val.toLowerCase();

return String(val ?? "").toLowerCase();

    return String(val).toLowerCase();
  };

  const vA = getSafeValue(a, sortBy);
  const vB = getSafeValue(b, sortBy);

  if (vA === vB) return 0;

  return sortDirection === "asc"
    ? vA > vB ? 1 : -1
    : vA < vB ? 1 : -1;
});

  const totalPages = Math.ceil(sorted.length / perPage);
  const currentPage = Math.min(page, totalPages || 1);
  const paginated = sorted.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage
  );

  const openToggleConfirm = (user) => {
  setToggleUser(user);
};

const confirmToggleStatus = async () => {
  if (!toggleUser) return;

  await toggleAdminUserStatus(toggleUser.id);

  const nextStatus =
    toggleUser.status === "active" ? "suspended" : "active";

  setUsers((prev) =>
    prev.map((u) =>
      u.id === toggleUser.id
        ? { ...u, status: nextStatus }
        : u
    )
  );

  showToastMessage(
  nextStatus === "active"
    ? TEXT.statusChangedToActive
    : TEXT.statusChangedToSuspended
);

  setToggleUser(null);
};

  const confirmDelete = async () => {
  await deleteAdminUser(deleteId);
  setUsers((prev) => prev.filter((u) => u.id !== deleteId));
  setDeleteId(null);

  showToastMessage(TEXT.userDeleted);
};

  const submitNewUser = async () => {
    if (!newUser.username.trim()) {
      showToastMessage(TEXT.usernameRequired, "error");
      return;
    }
    if (!newUser.password.trim()) {
     showToastMessage(TEXT.passwordRequired, "error");
      return;
    }

    try {
      setCreating(true);

      const created = await createAdminUser({
        username: newUser.username.trim(),
        email: newUser.email.trim() || null,
        password: newUser.password,
        role: newUser.role,
        is_active: newUser.is_active ? 1 : 0,
      });

      const safeUser = {
        id: created.id,
        name: created.name || "",
        email: created.email || "",
        role: created.role || "staff",
        status: created.status || "active",
        createdAt: created.createdAt || new Date().toISOString(),
      };

      setUsers((prev) => [safeUser, ...prev]);

      setShowAdd(false);
      setNewUser({
        username: "",
        email: "",
        password: "",
        role: "staff",
        is_active: true,
      });
    } catch (e) {
     showToastMessage(e.message, "error");
    } finally {
      setCreating(false);
    }
  };

  const changeSort = (field) => {
    if (sortBy === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) {
      return <FaSort className="inline opacity-40 ml-1" />;
    }
    return sortDirection === "asc" ? (
      <FaSortUp className="inline ml-1" />
    ) : (
      <FaSortDown className="inline ml-1" />
    );
  };

  const PERMISSION_LABELS = {
    can_manage_products:
      lang === "AR"
        ? "إدارة المنتجات والتصنيفات والمخزون"
        : "Manage Products, Categories & Inventory",
    can_manage_orders:
      lang === "AR"
        ? "إدارة الطلبات والسلال المتروكة"
        : "Manage Orders & Abandoned Carts",
    can_manage_customers:
      lang === "AR" ? "إدارة العملاء" : "Manage Customers",
    can_manage_messages:
      lang === "AR" ? "إدارة الرسائل" : "Manage Messages",
    can_manage_marketing:
      lang === "AR"
        ? "إدارة التسويق وأكواد الخصم"
        : "Manage Marketing & Promo Codes",
    can_manage_banners:
      lang === "AR"
        ? "إدارة البنرات (Top / Sale)"
        : "Manage Banners (Top / Sale)",
    can_manage_shipping:
      lang === "AR" ? "إدارة الشحن" : "Manage Shipping",
    can_view_analytics:
      lang === "AR"
        ? "عرض التحليلات ونشاط المستخدمين"
        : "View Analytics & User Activity",
    can_manage_seo: lang === "AR" ? "إدارة SEO" : "Manage SEO",
    can_manage_staff:
      lang === "AR" ? "إدارة المستخدمين والموظفين" : "Manage Users & Staff",
  };

  const PERMISSION_ICONS = {
  can_manage_products: FaBox,
  can_manage_orders: FaShoppingCart,
  can_manage_customers: FaUserFriends,
  can_manage_messages: FaEnvelope,
  can_manage_marketing: FaBullhorn,
  can_manage_banners: FaImage,
  can_manage_shipping: FaTruck,
  can_view_analytics: FaChartLine,
  can_manage_seo: FaSearch,
  can_manage_staff: FaUsersCog,
};

 const applyQuickFilter = (value) => {
  setPage(1);

  if (value === "All") {
    setRoleFilter("All");
    setStatusFilter("All");
    return;
  }

  if (["admin", "staff"].includes(value)) {
    setRoleFilter(value);
    setStatusFilter("All");
    return;
  }

  if (["active", "suspended"].includes(value)) {
    setStatusFilter(value);
    setRoleFilter("All");
  }
};

  const isQuickFilterActive = (value) => {
  if (value === "All") return roleFilter === "All" && statusFilter === "All";
  if (["admin", "staff"].includes(value)) {
    return roleFilter === value && statusFilter === "All";
  }
  if (["active", "suspended"].includes(value)) {
    return statusFilter === value && roleFilter === "All";
  }
  return false;
};

  return (
    <div
      className={`flex bg-[#f1f1f1] min-h-screen ${
        isOpen ? "overflow-hidden" : ""
      }`}
    >
      <AdminSidebar isOpen={isOpen} setIsOpen={setIsOpen} />

      <div
        className={`flex-1 ${
          lang === "EN" ? "lg:ml-64" : ""
        } w-full transition-all duration-300`}
      >
        <AdminHeader setIsOpen={setIsOpen} />

        <main className="p-4 sm:p-6 animate-fadeSlide">
          {!canAccess ? (
            <div className="mt-10 bg-red-50 border border-red-200 rounded-xl p-6 text-center">
              <p className="text-red-700 font-semibold text-lg">
                {TEXT.notAllowed}
              </p>
            </div>
          ) : (
            <>
              {/* PAGE HEADER */}
              <div className="flex items-center justify-between mb-5">
                <div className="mx-1 flex items-center gap-2">
                  <div className="flex items-center justify-center w-6 h-6">
                    <FaUsers className="text-gray-900 text-[18px]" />
                  </div>

                  <div className="flex items-baseline gap-2">
                    <h1 className="text-[20px] font-semibold text-gray-900">
                      {TEXT.title}
                    </h1>
                  </div>
                </div>

                <button
                  onClick={() => {
  setNewUser({
    username: "",
    email: "",
    password: "",
    role: "staff",
    is_active: true,
  });
  setConfirmPassword("");
  setShowNewPassword(false);
  setShowAdd(true);
}}
                  className="
                    h-8 px-3
                    bg-[#1f1f1f]
                    text-white
                    rounded-lg
                    shadow-[0_1px_0_rgba(0,0,0,0.08),0_3px_8px_rgba(0,0,0,0.2)]
                    text-sm font-medium
                    hover:bg-black
                    active:shadow-[inset_0_2px_6px_rgba(0,0,0,0.3)]
                    transition
                    flex items-center gap-2
                  "
                >
                  <FaPlus className="text-[12px]" />
                  {TEXT.addUser}
                </button>
              </div>

              {/* DESKTOP TABLE */}
              <div
                className="
                  hidden md:block
                  bg-white
                  border border-[#e3e3e3]
                  rounded-[14px]
                  overflow-visible
                  shadow-[0_12px_32px_rgba(0,0,0,0.10),0_2px_6px_rgba(0,0,0,0.06)]
                "
              >
                {/* TOP HEADER */}
                <div className="relative flex items-center justify-between px-4 py-3 border-b border-[#e3e3e3] bg-white rounded-t-[14px]">
                  {!searchOpen ? (
                    <div className="flex items-center gap-1 flex-wrap">
                      {[
  { label: TEXT.all, value: "All" },
  { label: TEXT.admin, value: "admin" },
  { label: TEXT.staff, value: "staff" },
  { label: TEXT.active, value: "active" },
  { label: TEXT.suspended, value: "suspended" },
].map((f) => {
                        const active = isQuickFilterActive(f.value);

                        return (
                          <button
                            key={f.value}
                            onClick={() => applyQuickFilter(f.value)}
                            className={`
                              px-3 py-[5px]
                              text-[13px] font-medium
                              rounded-md
                              transition
                              ${
                                active
                                  ? "bg-gray-200 text-gray-900"
                                  : "text-gray-600 hover:bg-gray-200"
                              }
                            `}
                          >
                            {f.label}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 w-full">
                      <div
                        className="
                          flex items-center justify-between
                          flex-1
                          h-8
                          px-3
                          bg-white
                          border border-gray-300
                          rounded-lg
                          shadow-sm
                          focus-within:border-gray-500
                          focus-within:ring-2
                          focus-within:ring-gray-200
                          transition
                        "
                      >
                        <div className="flex items-center gap-2 flex-1">
                          <FaSearch className="text-gray-400 text-sm" />
                          <input
                            autoFocus
                            type="text"
                            value={search}
                            onChange={(e) => {
  const value = e?.target?.value ?? "";
  setSearch(value);
}}
                            placeholder={TEXT.search}
                            className="flex-1 outline-none text-sm bg-transparent"
                          />
                        </div>

                        <button
                          onClick={() => {
                            setSearch("");
                            setSearchOpen(false);
                          }}
                          className="w-5 h-5 flex items-center justify-center rounded-full border border-gray-500 text-gray-500 hover:bg-gray-100"
                        >
                          ✕
                        </button>
                      </div>

                      <button
                        onClick={() => {
                          setSearch("");
                          setSearchOpen(false);
                        }}
                        className="px-3 h-8 bg-white border border-gray-300 rounded-lg shadow-sm text-sm font-medium mx-1"
                      >
                        {TEXT.cancel}
                      </button>
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    {!searchOpen && (
                      <button
                        onClick={() => setSearchOpen(true)}
                        className="
                          flex items-center justify-center gap-1
                          px-2 h-9
                          bg-white
                          border border-gray-300
                          rounded-lg
                          shadow-[0_1px_0_rgba(0,0,0,0.05),0_2px_4px_rgba(0,0,0,0.06)]
                          hover:bg-gray-50
                          active:shadow-[inset_0_2px_4px_rgba(0,0,0,0.08)]
                          transition
                        "
                      >
                        <FaSearch className="text-gray-600 text-sm" />
                        <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                          <path
                            d="M3 5H17M6 10H14M9 15H11"
                            stroke="#4B5563"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>

                <table
                  dir={lang === "AR" ? "rtl" : "ltr"}
                  className="w-full min-w-[860px] text-center"
                >
                  <thead className="bg-[#f6f6f7] text-[#6d7175] text-[13px] font-medium">
                    <tr>
                      <th className="w-[60px]"></th>
                      <th
                        className="px-4 py-2 cursor-pointer select-none"
                        onClick={() => changeSort("name")}
                      >
                        {TEXT.name}
                        {getSortIcon("name")}
                      </th>
                      <th
                        className="px-4 py-2 cursor-pointer select-none"
                        onClick={() => changeSort("email")}
                      >
                        {TEXT.email}
                        {getSortIcon("email")}
                      </th>
                      <th
                        className="px-4 py-2 cursor-pointer select-none"
                        onClick={() => changeSort("role")}
                      >
                        {TEXT.role}
                        {getSortIcon("role")}
                      </th>
                      <th
                        className="px-4 py-2 cursor-pointer select-none"
                        onClick={() => changeSort("status")}
                      >
                        {TEXT.status}
                        {getSortIcon("status")}
                      </th>
                      <th
                        className="px-4 py-2 cursor-pointer select-none"
                        onClick={() => changeSort("createdAt")}
                      >
                        {TEXT.created}
                        {getSortIcon("createdAt")}
                      </th>
                      <th className="px-4 py-2 w-[170px] rounded-tr-[14px]">
                        {TEXT.actions}
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
  [...Array(6)].map((_, i) => (
    <tr key={i} className="animate-pulse border-b border-[#f1f1f1]">
      <td className="px-2 py-3">
        <div className="w-8 h-8 mx-auto rounded-full bg-gray-200" />
      </td>

      <td className="px-4 py-3">
        <div className="h-3 w-24 mx-auto bg-gray-200 rounded" />
      </td>

      <td className="px-4 py-3">
        <div className="h-3 w-32 mx-auto bg-gray-200 rounded" />
      </td>

      <td className="px-4 py-3">
        <div className="h-3 w-16 mx-auto bg-gray-200 rounded" />
      </td>

      <td className="px-4 py-3">
        <div className="h-3 w-20 mx-auto bg-gray-200 rounded" />
      </td>

      <td className="px-4 py-3">
        <div className="h-3 w-24 mx-auto bg-gray-200 rounded" />
      </td>

      <td className="px-4 py-3">
        <div className="h-8 w-20 mx-auto bg-gray-200 rounded" />
      </td>
    </tr>
  ))
): paginated.length === 0 ? (
                      <tr>
  <td colSpan="7" className="p-10 text-center">
    <div className="flex flex-col items-center gap-2">
      <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
        <FaUsers className="text-gray-400" />
      </div>

      <p className="text-sm font-medium text-gray-900">
        {TEXT.empty}
      </p>

      <p className="text-xs text-gray-400">
        {lang === "EN"
          ? "Try adjusting filters or search"
          : "حاول تغيير الفلاتر أو البحث"}
      </p>
    </div>
  </td>
</tr>
                    ) : (
                      paginated.map((u) => (
                        <tr
                          key={u.id}
                          className="group border-b border-[#f1f1f1] hover:bg-[#f6f6f7] transition text-[13px]"
                        >
{/* AVATAR COLUMN */}
<td className="px-2 py-3 text-center">
  <div
    className="flex justify-center"
    onMouseEnter={() => setHoverUser(u)}
  >
    <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-900 font-semibold text-xs uppercase">
      {(u.name || "U").charAt(0)}
    </div>
  </div>
</td>

{/* NAME COLUMN */}
<td className="px-4 py-3 text-center relative">
  <div
    className="flex items-center justify-center gap-2 cursor-pointer"
    onMouseEnter={() => setHoverUser(u)}
  >
    <span className="font-medium text-gray-900">
      {u.name}
    </span>

    {hoverUser?.id === u.id && (
      <div
        ref={overlayRef}
        className="
          absolute top-full mt-2 z-50
          bg-white
          border border-gray-200
          rounded-xl
          shadow-[0_10px_30px_rgba(0,0,0,0.15)]
          p-3
          min-w-[220px]
          text-left
        "
      >
        {/* Username */}
        <div className="text-xs text-gray-500 mb-1">
          {TEXT.username}
        </div>
        <div className="text-sm font-semibold text-gray-900 mb-2">
          {u.name}
        </div>

        {/* Password */}
        <div className="text-xs text-gray-500 mb-1">
          {TEXT.password}
        </div>

        <div className="text-xs text-gray-900 break-all mb-3">
          ••••••••••••••••••••
        </div>

        <button
          onClick={(e) => {
            e.stopPropagation();
            openResetPasswordModal(u);
          }}
          className="
            w-full
            h-[34px]
            bg-[#f6f6f7]
            border border-gray-300
            rounded-lg
            text-[13px] font-medium text-gray-800
            shadow-[0_1px_0_rgba(0,0,0,0.05),0_3px_6px_rgba(0,0,0,0.08)]
            hover:bg-gray-100
            active:shadow-[inset_0_2px_4px_rgba(0,0,0,0.12)]
            transition
          "
        >
          {TEXT.resetPassword}
        </button>
      </div>
    )}
  </div>
</td>

                          <td className="px-4 py-3 text-sm text-gray-600">
                            {u.email || (
                              <span className="text-gray-300">
                                {TEXT.noEmail}
                              </span>
                            )}
                          </td>

                          <td className="px-4 py-3">
                            <span className="inline-flex items-center justify-center px-2.5 py-[2px] rounded-md text-[11px] font-medium bg-gray-100 text-gray-900 capitalize">
                              {u.role}
                            </span>
                          </td>

                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex items-center justify-center px-2.5 py-[2px] rounded-md text-[11px] font-medium ${
                                u.status === "active"
                                  ? "bg-green-50 text-green-700"
                                  : "bg-red-50 text-red-700"
                              }`}
                            >
                              {u.status === "active"
                                ? TEXT.active
                                : TEXT.suspended}
                            </span>
                          </td>

                          <td className="px-4 py-3 text-sm text-gray-500">
                            {new Date(u.createdAt).toLocaleDateString()}
                          </td>

                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={() => openToggleConfirm(u)}
                                className="
                                  w-8 h-8 flex items-center justify-center
                                  rounded-md
                                  bg-white border border-gray-300
                                  shadow-sm hover:bg-gray-50 transition
                                "
                                title={u.status === "active" ? TEXT.suspended : TEXT.active}
                              >
                                {u.status === "active" ? (
                                  <FaUserSlash className="text-orange-600 text-[14px]" />
                                ) : (
                                  <FaUserShield className="text-green-600 text-[14px]" />
                                )}
                              </button>

                              <button
                                onClick={() => setDeleteId(u.id)}
                                className="
                                  w-8 h-8 flex items-center justify-center
                                  rounded-md
                                  bg-white border border-gray-300
                                  shadow-sm hover:bg-gray-50 transition
                                "
                                title={TEXT.delete}
                              >
                                <FaTrash className="text-red-600 text-[13px]" />
                              </button>

                              {u.role === "staff" && canAccess && (
                                <button
                                  onClick={() => openPermissions(u)}
                                  className="
                                    w-8 h-8 flex items-center justify-center
                                    rounded-md
                                    bg-white border border-gray-300
                                    shadow-sm hover:bg-gray-50 transition
                                  "
                                  title={TEXT.permissions}
                                >
                                  <FaKey className="text-gray-900 text-[13px]" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>

                  <tfoot>
                    <tr className="bg-[#f6f6f7] border-t border-[#e3e3e3]">
                      <td
                        colSpan="7"
                        className={`px-4 py-2 ${
                          lang === "EN"
                            ? "rounded-br-[14px] rounded-bl-[14px]"
                            : "rounded-bl-[14px] rounded-br-[14px]"
                        }`}
                      >
                        <div
                          className={`flex items-center justify-between text-sm text-gray-600 ${
                            lang === "AR" ? "flex-row-reverse" : ""
                          }`}
                        >
                          <span className="text-gray-500 text-sm">
                            {filtered.length} {TEXT.usersCount}
                          </span>

                          <div className="flex items-center gap-0.5">
                            <button
                              onClick={() =>
                                setPage((p) => Math.max(1, p - 1))
                              }
                              disabled={currentPage === 1}
                              className={`w-7 h-7 flex font-bold items-center justify-center rounded-md border bg-gray-200 ${
                                currentPage === 1
                                  ? "opacity-40 cursor-not-allowed"
                                  : "hover:bg-gray-300"
                              }`}
                            >
                              {"<"}
                            </button>

                            <button
                              onClick={() =>
                                setPage((p) => Math.min(totalPages || 1, p + 1))
                              }
                              disabled={currentPage === totalPages || totalPages === 0}
                              className={`w-7 h-7 flex items-center font-bold justify-center rounded-md border bg-gray-200 ${
                                currentPage === totalPages || totalPages === 0
                                  ? "opacity-40 cursor-not-allowed"
                                  : "hover:bg-gray-300"
                              }`}
                            >
                              {">"}
                            </button>

                            <span className="mx-2 text-gray-600 font-bold">
                              {currentPage}-{totalPages || 1}
                            </span>
                          </div>
                        </div>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* MOBILE */}
              <div className="md:hidden">
                <div className="bg-white border border-gray-200 rounded-xl p-3 mb-3 shadow-sm space-y-3">
                  <div className="flex items-center gap-2 border rounded-lg px-3 h-10">
                    <FaSearch className="text-gray-400 text-sm" />
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => {
  const value = e?.target?.value ?? "";
  setSearch(value);
                        setPage(1);
                      }}
                      placeholder={TEXT.search}
                      className="flex-1 outline-none text-sm bg-transparent"
                    />
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    {[
  { label: TEXT.all, value: "All" },
  { label: TEXT.admin, value: "admin" },
  { label: TEXT.staff, value: "staff" },
  { label: TEXT.active, value: "active" },
  { label: TEXT.suspended, value: "suspended" },
].map((f) => (
                      <button
                        key={f.value}
                        onClick={() => applyQuickFilter(f.value)}
                        className={`px-3 py-1 text-xs rounded-md transition ${
                          isQuickFilterActive(f.value)
                            ? "bg-gray-900 text-white"
                            : "bg-gray-100 text-gray-900"
                        }`}
                      >
                        {f.label}
                      </button>
                    ))}
                  </div>

                  <div className="text-xs text-gray-500">
                    {filtered.length} {TEXT.usersCount}
                  </div>
                </div>

                {loading ? (
  [...Array(4)].map((_, i) => (
    <div
      key={i}
      className="bg-white border border-[#e3e3e3] rounded-[14px] p-4 mb-3 animate-pulse"
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="w-11 h-11 rounded-full bg-gray-200" />
        <div className="flex-1">
          <div className="h-3 w-32 bg-gray-200 rounded mb-2" />
          <div className="h-3 w-24 bg-gray-200 rounded" />
        </div>
      </div>

      <div className="h-3 w-full bg-gray-200 rounded mb-2" />
      <div className="h-3 w-3/4 bg-gray-200 rounded mb-2" />

      <div className="flex justify-end gap-2 mt-3">
        <div className="w-9 h-9 bg-gray-200 rounded" />
        <div className="w-9 h-9 bg-gray-200 rounded" />
      </div>
    </div>
  ))
) : paginated.length === 0 ? (
                  <div className="text-center p-8">
  <div className="flex flex-col items-center gap-2">
    <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
      <FaUsers className="text-gray-400" />
    </div>

    <p className="text-sm font-medium text-gray-900">
      {TEXT.empty}
    </p>

    <p className="text-xs text-gray-400">
      {lang === "EN"
        ? "Try adjusting filters or search"
        : "حاول تغيير الفلاتر أو البحث"}
    </p>
  </div>
</div>
                ) : (
                  paginated.map((u) => (
                    <div
                      key={u.id}
                      className="bg-white border border-[#e3e3e3] rounded-[14px] shadow-[0_8px_20px_rgba(0,0,0,0.08)] p-4 space-y-3 mb-3"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <div className="w-11 h-11 rounded-full bg-gray-200 flex items-center justify-center text-gray-900 font-semibold uppercase">
                            {(u.name || "U").charAt(0)}
                          </div>
                          <div>
                            <div className="font-semibold text-gray-900">
                              {u.name}
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5">
                              {u.email || TEXT.noEmail}
                            </div>
                          </div>
                        </div>

                        <span
                          className={`inline-flex items-center justify-center px-2.5 py-[2px] rounded-md text-[11px] font-medium ${
                            u.status === "active"
                              ? "bg-green-50 text-green-700"
                              : "bg-red-50 text-red-700"
                          }`}
                        >
                          {u.status === "active"
                            ? TEXT.active
                            : TEXT.suspended}
                        </span>
                      </div>

                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">{TEXT.role}</span>
                        <span className="inline-flex items-center justify-center px-2.5 py-[2px] rounded-md text-[11px] font-medium bg-gray-100 text-gray-900 capitalize">
                          {u.role}
                        </span>
                      </div>

                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">{TEXT.created}</span>
                        <span className="text-gray-900">
                          {new Date(u.createdAt).toLocaleDateString()}
                        </span>
                      </div>

                      <div className="flex items-center justify-end gap-2 pt-2 border-t">
                        <button
                          onClick={() => openToggleConfirm(u)}
                          className="
                            w-9 h-9 flex items-center justify-center
                            rounded-md
                            bg-white border border-gray-300
                            shadow-sm hover:bg-gray-50 transition
                          "
                        >
                          {u.status === "active" ? (
                            <FaUserSlash className="text-orange-600 text-[14px]" />
                          ) : (
                            <FaUserShield className="text-green-600 text-[14px]" />
                          )}
                        </button>

                        <button
                          onClick={() => setDeleteId(u.id)}
                          className="
                            w-9 h-9 flex items-center justify-center
                            rounded-md
                            bg-white border border-gray-300
                            shadow-sm hover:bg-gray-50 transition
                          "
                        >
                          <FaTrash className="text-red-600 text-[13px]" />
                        </button>

                        {u.role === "staff" && canAccess && (
                          <button
                            onClick={() => openPermissions(u)}
                            className="
                              w-9 h-9 flex items-center justify-center
                              rounded-md
                              bg-white border border-gray-300
                              shadow-sm hover:bg-gray-50 transition
                            "
                          >
                            <FaKey className="text-gray-900 text-[13px]" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                )}

                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4 px-1 text-sm">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className={`px-3 py-1 rounded-md border ${
                        currentPage === 1
                          ? "opacity-40"
                          : "bg-white hover:bg-gray-100"
                      }`}
                    >
                      {TEXT.prev}
                    </button>

                    <span className="font-medium">
                      {currentPage} / {totalPages}
                    </span>

                    <button
                      onClick={() =>
                        setPage((p) => Math.min(totalPages, p + 1))
                      }
                      disabled={currentPage === totalPages}
                      className={`px-3 py-1 rounded-md border ${
                        currentPage === totalPages
                          ? "opacity-40"
                          : "bg-white hover:bg-gray-100"
                      }`}
                    >
                      {TEXT.next}
                    </button>
                  </div>
                )}
              </div>
            </>
          )}

          {/* ADD USER MODAL */}
          {showAdd && (
  <div
  className="fixed inset-0 bg-black/40 flex items-center justify-center z-40"
  onClick={() => {
    setShowAdd(false);
    setConfirmPassword("");
    setShowNewPassword(false);
    setNewUser({
      username: "",
      email: "",
      password: "",
      role: "staff",
      is_active: true,
    });
  }}
>
    <div   onClick={(e) => e.stopPropagation()}
    className="bg-white p-6 rounded-2xl shadow-xl w-[95%] max-w-md animate-fadeSlide border border-[#e3e3e3]">
      
      <h2 className="text-lg font-semibold text-gray-900 mb-4">
        {TEXT.addUser}
      </h2>

      <div className="space-y-3">

        {/* USERNAME */}
        <div>
          <label className="text-sm font-semibold text-gray-600 block mb-1">
            {TEXT.username}
          </label>
          <input
            ref={firstInputRef}
            className="w-full h-11 px-3 border border-gray-300 rounded-xl shadow-sm outline-none focus:ring-2 focus:ring-gray-200"
            value={newUser.username || ""}
            onChange={(e) => {
              const value = e?.target?.value ?? "";
              setNewUser((prev) => ({ ...prev, username: value }));
            }}
          />
        </div>

        {/* EMAIL */}
        <div>
          <label className="text-sm font-semibold text-gray-600 block mb-1">
            {TEXT.email}
          </label>
          <input
            className="w-full h-11 px-3 border border-gray-300 rounded-xl shadow-sm outline-none focus:ring-2 focus:ring-gray-200"
            placeholder={TEXT.emailOptional}
            value={newUser.email || ""}
            onChange={(e) => {
              const value = e?.target?.value ?? "";
              setNewUser((prev) => ({ ...prev, email: value }));
            }}
          />
        </div>

        {/* PASSWORD */}
        <div>
          <label className="text-sm font-semibold text-gray-600 block mb-1">
            {TEXT.password}
          </label>

          <div className="relative">
            <input
              type={showNewPassword ? "text" : "password"}
              className="w-full h-11 pl-3 pr-10 border border-gray-300 rounded-xl shadow-sm outline-none focus:ring-2 focus:ring-gray-200"
              value={newUser.password || ""}
              onChange={(e) => {
                const value = e?.target?.value ?? "";
                setNewUser((prev) => ({ ...prev, password: value }));
              }}
            />

            {newUser.password && (
              <button
                type="button"
                onClick={() => setShowNewPassword((p) => !p)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
              >
                {showNewPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            )}
          </div>
        </div>

        {/* CONFIRM PASSWORD */}
        <div>
          <label className="text-sm font-semibold text-gray-600 block mb-1">
            {TEXT.confirmPassword}
          </label>

          <div className="relative">
            <input
              type="password"
              className={`
                w-full h-11 pl-3 pr-10 border rounded-xl shadow-sm outline-none focus:ring-2
                ${
                  confirmPassword && confirmPassword !== newUser.password
                    ? "border-red-400 focus:ring-red-200"
                    : "border-gray-300 focus:ring-gray-200"
                }
              `}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </div>

          {/* ERROR TEXT */}
          {confirmPassword && confirmPassword !== newUser.password && (
            <p className="text-xs text-red-500 mt-1">
             {TEXT.confirmPasswordError}
            </p>
          )}
        </div>

        {/* ROLE + STATUS */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
  <div>
    <label className="text-sm font-semibold text-gray-600 block mb-1">
      {TEXT.role}
    </label>

    <div className="relative">
      <select
        className="
          w-full h-11 px-3 pr-9
          border border-gray-300 rounded-xl shadow-sm
          bg-white
          outline-none
          focus:ring-2 focus:ring-gray-200
          appearance-none
          text-sm text-gray-800
        "
        value={newUser.role}
        onChange={(e) => {
          const value = e?.target?.value ?? "staff";
          setNewUser((prev) => ({ ...prev, role: value }));
        }}
      >
        <option value="admin">{TEXT.admin}</option>
        <option value="staff">{TEXT.staff}</option>
      </select>

      <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-xs">
        ▼
      </div>
    </div>
  </div>

  <div>
    <label className="text-sm font-semibold text-gray-600 block mb-1">
      {TEXT.status}
    </label>

    <button
      type="button"
      onClick={() =>
        setNewUser((prev) => ({ ...prev, is_active: !prev.is_active }))
      }
      className={`
        w-full h-11 px-3
        border rounded-xl shadow-sm
        flex items-center justify-between
        transition
        ${
          newUser.is_active
            ? "border-green-200 bg-green-50 text-green-700"
            : "border-gray-300 bg-white text-gray-900"
        }
      `}
    >
      <div className="flex items-center gap-2">
        <span
          className={`
            relative inline-flex h-5 w-9 items-center rounded-full transition
            ${newUser.is_active ? "bg-green-600" : "bg-gray-300"}
          `}
        >
          <span
  className={`
    inline-block h-4 w-4 transform rounded-full bg-white transition
    ${
      newUser.is_active
        ? lang === "AR"
          ? "-translate-x-4"
          : "translate-x-4"
        : lang === "AR"
        ? "-translate-x-0.5"
        : "translate-x-0.5"
    }
  `}
/>
        </span>

        <span className="text-sm font-medium">
          {newUser.is_active ? TEXT.active : TEXT.suspended}
        </span>
      </div>
    </button>
  </div>
</div>
      </div>

      {/* ACTIONS */}
      <div className="flex justify-end gap-3 mt-6">
        <button
          className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300"
          onClick={() => {
  setShowAdd(false);
  setConfirmPassword("");
  setShowNewPassword(false);
  setNewUser({
    username: "",
    email: "",
    password: "",
    role: "staff",
    is_active: true,
  });
}}
        >
          {TEXT.cancel}
        </button>

        <button
          className="px-4 py-2 rounded-lg bg-[#1f1f1f] text-white hover:bg-black disabled:opacity-40"
          onClick={submitNewUser}
          disabled={
            creating ||
            !newUser.username ||
            !newUser.password ||
            confirmPassword !== newUser.password
          }
        >
          {creating ? TEXT.creating : TEXT.create}
        </button>
      </div>
    </div>
  </div>
)}

{resetUser && (
  <div
  className="fixed inset-0 bg-black/40 flex items-center justify-center z-40"
  onClick={() => {
    setResetUser(null);
    setShowResetPassword(false);
    setResetForm({
      id: null,
      username: "",
      email: "",
      role: "staff",
      is_active: true,
      password: "",
    });
  }}
>
    <div  onClick={(e) => e.stopPropagation()}
    className="bg-white p-6 rounded-2xl shadow-xl w-[95%] max-w-md animate-fadeSlide border border-[#e3e3e3]">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">
        {TEXT.resetPasswordTitle}
      </h2>

      <div className="space-y-3">
        <div>
          <label className="text-sm font-semibold text-gray-600 block mb-1">
            {TEXT.username}
          </label>
          <input
            className="w-full h-11 px-3 border border-gray-300 rounded-xl shadow-sm bg-gray-50 outline-none"
            value={resetForm.username || ""}
            readOnly
          />
        </div>

        <div>
          <label className="text-sm font-semibold text-gray-600 block mb-1">
            {TEXT.email}
          </label>
          <input
            className="w-full h-11 px-3 border border-gray-300 rounded-xl shadow-sm bg-gray-50 outline-none"
            value={resetForm.email || ""}
            readOnly
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-semibold text-gray-600 block mb-1">
              {TEXT.role}
            </label>
            <input
              className="w-full h-11 px-3 border border-gray-300 rounded-xl shadow-sm bg-gray-50 outline-none"
              value={resetForm.role || ""}
              readOnly
            />
          </div>

          <div>
            <label className="text-sm font-semibold text-gray-600 block mb-1">
              {TEXT.status}
            </label>
            <input
              className="w-full h-11 px-3 border border-gray-300 rounded-xl shadow-sm bg-gray-50 outline-none"
              value={resetForm.is_active ? TEXT.active : TEXT.suspended}
              readOnly
            />
          </div>
        </div>

        <div>
          <label className="text-sm font-semibold text-gray-600 block mb-1">
            {TEXT.password}
          </label>
          <input
            type="password"
            className="w-full h-11 px-3 border border-gray-300 rounded-xl shadow-sm outline-none focus:ring-2 focus:ring-gray-200"
            value={resetForm.password || ""}
            onChange={(e) => {
              const value = e?.target?.value ?? "";
              setResetForm((prev) => ({ ...prev, password: value }));
            }}
            placeholder={TEXT.password}
          />
        </div>
      </div>

      <div className="flex justify-end gap-3 mt-6">
        <button
          className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300"
          onClick={() => {
  setResetUser(null);
  setShowResetPassword(false);
  setResetForm({
    id: null,
    username: "",
    email: "",
    role: "staff",
    is_active: true,
    password: "",
  });
}}
          disabled={resettingPassword}
        >
          {TEXT.cancel}
        </button>

        <button
          className="px-4 py-2 rounded-lg bg-[#1f1f1f] text-white hover:bg-black"
          onClick={submitResetPassword}
          disabled={resettingPassword}
        >
          {resettingPassword ? TEXT.creating : TEXT.save}
        </button>
      </div>
    </div>
  </div>
)}

          {/* DELETE MODAL */}
          {deleteId !== null && (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-40">
              <div className="bg-white p-6 rounded-xl shadow-xl w-80 animate-fadeSlide border border-[#e3e3e3]">
                <h2 className="text-lg font-bold mb-3 text-red-600">
                  {TEXT.deleteTitle}
                </h2>
                <p className="text-gray-900 mb-6">{TEXT.deleteText}</p>

                <div className="flex justify-end gap-3">
                  <button
                    className="px-4 py-2 rounded-lg bg-gray-300 hover:bg-gray-400"
                    onClick={() => setDeleteId(null)}
                  >
                    {TEXT.cancel}
                  </button>
                  <button
                    className="px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600"
                    onClick={confirmDelete}
                  >
                    {TEXT.delete}
                  </button>
                </div>
              </div>
            </div>
          )}

          {toggleUser !== null && (
  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-40">
    <div className="bg-white p-6 rounded-xl shadow-xl w-80 animate-fadeSlide border border-[#e3e3e3]">
      <h2 className="text-lg font-bold mb-3 text-gray-900">
        {TEXT.toggleTitle}
      </h2>

      <p className="text-gray-900 mb-6">
        {toggleUser.status === "active"
          ? TEXT.toggleToSuspendedText
          : TEXT.toggleToActiveText}
      </p>

      <div className="flex justify-end gap-3">
        <button
          className="px-4 py-2 rounded-lg bg-gray-300 hover:bg-gray-400"
          onClick={() => setToggleUser(null)}
        >
          {TEXT.cancel}
        </button>

        <button
          className={`px-4 py-2 rounded-lg text-white ${
            toggleUser.status === "active"
              ? "bg-orange-500 hover:bg-orange-600"
              : "bg-green-600 hover:bg-green-700"
          }`}
          onClick={confirmToggleStatus}
        >
          {toggleUser.status === "active" ? TEXT.suspended : TEXT.active}
        </button>
      </div>
    </div>
  </div>
)}

{toast && (
  <div className="fixed bottom-6 right-6 z-[9999] animate-fadeSlide">
    <div
      className={`
        flex items-center gap-3
        px-4 py-3
        rounded-[12px]
        shadow-[0_10px_30px_rgba(0,0,0,0.12)]
        border
        min-w-[240px]
        ${
          toast.type === "error"
            ? "bg-red-50 border-red-200"
            : "bg-white border-gray-200"
        }
      `}
    >
      <div
        className={`
          flex items-center justify-center
          w-7 h-7 rounded-full
          ${
            toast.type === "error"
              ? "bg-red-100"
              : "bg-green-100"
          }
        `}
      >
        {toast.type === "error" ? (
          <span className="text-red-600 text-xs">!</span>
        ) : (
          <FaCheck className="text-green-700 text-sm" />
        )}
      </div>

      <div className="text-sm text-gray-800 font-medium">
        {toast.message}
      </div>
    </div>
  </div>
)}
        </main>

        {/* PERMISSIONS MODAL */}
        {permUser && (
          <div
  className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
  onClick={() => setPermUser(null)}
>
            <div onClick={(e) => e.stopPropagation()}
            className="
  bg-white
  p-6
  rounded-2xl
  w-[95%] max-w-lg
  border border-[#e3e3e3]
  shadow-[0_20px_50px_rgba(0,0,0,0.15)]
  max-h-[85vh]
  overflow-y-auto
">
              <div className="mb-5">
  <h2 className="text-lg font-semibold text-gray-900">
    {TEXT.permissions}
  </h2>
  <p className="text-sm text-gray-500 mt-1">
    {permUser.name}
  </p>
</div>

<div className="flex items-center justify-between mb-2">
  <span className="text-xs text-gray-500">
    {lang === "EN" ? "Quick actions" : "إجراءات سريعة"}
  </span>

  <div className="flex gap-2">
    <button
      onClick={() =>
        setPermissions((prev) =>
          Object.fromEntries(Object.keys(prev).map((k) => [k, true]))
        )
      }
      className="text-xs px-3 py-1 rounded-md bg-gray-100 hover:bg-gray-200"
    >
      {lang === "EN" ? "Enable all" : "تفعيل الكل"}
    </button>

    <button
      onClick={() =>
        setPermissions((prev) =>
          Object.fromEntries(Object.keys(prev).map((k) => [k, false]))
        )
      }
      className="text-xs px-3 py-1 rounded-md bg-gray-100 hover:bg-gray-200"
    >
      {lang === "EN" ? "Disable all" : "تعطيل الكل"}
    </button>
  </div>
</div>

              <div className="space-y-4">

  {Object.keys(permissions).map((key) => {
    const active = permissions[key];

    return (
      <div
        key={key}
        className={`
          flex items-center justify-between
          p-3 rounded-xl border
          transition
          ${active 
            ? "bg-gray-50 border-gray-300" 
            : "bg-white border-gray-200 hover:bg-gray-50"}
        `}
      >
        {/* LABEL */}
        <div className="flex flex-col">
          <div className="flex items-center gap-3">
  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100">
    {React.createElement(PERMISSION_ICONS[key], {
      className: "text-gray-600 text-sm",
    })}
  </div>

  <span className="text-sm font-medium text-gray-900">
    {PERMISSION_LABELS[key]}
  </span>
</div>
        </div>

        {/* TOGGLE */}
        <button
          type="button"
          onClick={() =>
            setPermissions((prev) => ({
              ...prev,
              [key]: !prev[key],
            }))
          }
          className={`
            relative inline-flex h-6 w-11 items-center rounded-full transition
            ${active ? "bg-black" : "bg-gray-300"}
          `}
        >
          <span
            className={`
              inline-block h-4 w-4 transform rounded-full bg-white transition
              ${active ? "translate-x-6" : "translate-x-1"}
            `}
          />
        </button>
      </div>
    );
  })}

</div>

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                <button
                  className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300"
                  onClick={() => setPermUser(null)}
                >
                  {TEXT.cancel}
                </button>
                <button
                  className="bg-[#1f1f1f] text-white px-4 py-2 rounded-lg hover:bg-black"
                  onClick={async () => {
                    await updateStaffPermissions(permUser.id, permissions);

                    const adminUser = JSON.parse(
                      localStorage.getItem("adminUser") || "{}"
                    );

                    if (adminUser.id === permUser.id) {
                      localStorage.setItem(
                        "staffPermissions",
                        JSON.stringify(permissions)
                      );
                    }

                    setPermUser(null);
                  }}
                >
                  {TEXT.save}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminUsers;